import { motion } from 'framer-motion'
import { ChevronDown, Play } from 'lucide-react'
import { useState } from 'react'

export function Hero() {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/20 via-gray-900 to-accent-900/20" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-primary-500/30 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-accent-500/30 rounded-full blur-3xl animate-pulse-slow animation-delay-2000" />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6">
              Capture Reality in{' '}
              <span className="bg-gradient-to-r from-primary-400 to-accent-500 bg-clip-text text-transparent">
                4D
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
          >
            Revolutionary volumetric capture technology powered by 4D Gaussian Splatting. 
            Transform your visual content with real-time, photorealistic 3D scenes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <a
              href="#contact"
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-full font-medium text-lg hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300 transform hover:scale-105"
            >
              Start Your Project
            </a>
            <button
              onClick={() => setIsVideoModalOpen(true)}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-medium text-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <Play size={20} className="group-hover:scale-110 transition-transform" />
              Watch Demo
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto"
          >
            <div>
              <div className="text-3xl font-bold text-white mb-1">60 FPS</div>
              <div className="text-gray-400">Real-time Rendering</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">4K+</div>
              <div className="text-gray-400">Resolution Support</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">&lt; 100ms</div>
              <div className="text-gray-400">Edge Latency</div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <a
            href="#features"
            className="flex flex-col items-center text-gray-400 hover:text-white transition-colors"
          >
            <span className="text-sm mb-2">Explore</span>
            <ChevronDown className="animate-bounce" />
          </a>
        </motion.div>
      </div>

      {/* Video Modal Placeholder */}
      {isVideoModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div className="bg-gray-800 rounded-xl p-8 max-w-2xl w-full">
            <p className="text-white text-center">Video demo will be integrated here</p>
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-full mx-auto block"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  )
}